(() => {
  if (customElements.get('pbh-sticky-buy-bar')) return;

  class PbhStickyBuyBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialised === 'true') return;

      this.#productFormEl = document.querySelector('product-form[data-section-id]');
      if (!this.#productFormEl) return;

      this.#mainSectionId = this.#productFormEl.dataset.sectionId;
      this.#mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.#mainSectionId}`);
      this.#stickySubmitButton = this.querySelector('[data-sticky-buy-submit]');
      this.#priceContainer = this.querySelector('[data-sticky-buy-price]');
      this.#mediaContainer = this.querySelector('[data-sticky-buy-media]');

      const productForm = this.#productFormEl.querySelector('form[data-type="add-to-cart-form"]');
      if (!productForm || !this.#mainSubmitButton || !this.#stickySubmitButton) return;

      this.dataset.initialised = 'true';
      this.#productForm = productForm;
      this.#prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.#prefersReducedMotion) {
        this.classList.add('sticky-buy-bar--reduced-motion');
      }

      this.#stickySubmitButton.setAttribute('form', productForm.getAttribute('id'));
      this.#syncSubmitState();
      this.#wireFormSubmit();
      this.#observeSubmitButton();
      this.#observeMainSubmitState();
      this.#subscribeEvents();
    }

    disconnectedCallback() {
      if (this.#intersectionObserver) {
        this.#intersectionObserver.disconnect();
        this.#intersectionObserver = null;
      }

      if (this.#submitStateObserver) {
        this.#submitStateObserver.disconnect();
        this.#submitStateObserver = null;
      }

      this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
      this.#unsubscribers = [];
    }

    #productFormEl = null;
    #productForm = null;
    #mainSectionId = '';
    #mainSubmitButton = null;
    #stickySubmitButton = null;
    #priceContainer = null;
    #mediaContainer = null;
    #intersectionObserver = null;
    #submitStateObserver = null;
    #unsubscribers = [];
    #prefersReducedMotion = false;

    #observeSubmitButton() {
      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle('sticky-buy-bar--visible', !entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );

      this.#intersectionObserver.observe(this.#mainSubmitButton);
    }

    #observeMainSubmitState() {
      if (!this.#mainSubmitButton) return;

      this.#submitStateObserver = new MutationObserver(() => {
        this.#syncSubmitState();
        if (!this.#mainSubmitButton.classList.contains('loading')) {
          this.#setStickyLoading(false);
        }
      });

      this.#submitStateObserver.observe(this.#mainSubmitButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
      });
    }

    #wireFormSubmit() {
      this.#productForm.addEventListener('submit', () => {
        this.#setStickyLoading(true);
      });
    }

    #subscribeEvents() {
      this.#unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.#mainSectionId) return;

          const { html, variant } = event.data;
          this.#updatePrice(html);
          this.#updateMedia(variant);
          this.#syncSubmitState(html);
        })
      );

      this.#unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'product-form') return;
          this.#setStickyLoading(false);
        })
      );

      this.#unsubscribers.push(
        subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event.source !== 'product-form') return;
          this.#setStickyLoading(false);
          this.#syncSubmitState();
        })
      );
    }

    #updatePrice(html) {
      if (!this.#priceContainer) return;

      const sourcePrice = html.getElementById(`price-${this.#mainSectionId}`);
      if (!sourcePrice) return;

      this.#priceContainer.innerHTML = sourcePrice.innerHTML;
    }

    #updateMedia(variant) {
      if (!this.#mediaContainer || !variant?.featured_media?.preview_image?.src) return;

      const image = this.#mediaContainer.querySelector('img');
      if (!image) return;

      image.src = variant.featured_media.preview_image.src;
      if (variant.featured_media.alt) {
        image.alt = variant.featured_media.alt;
      }
    }

    #syncSubmitState(html) {
      const sourceButton =
        html?.getElementById(`ProductSubmitButton-${this.#mainSectionId}`) ?? this.#mainSubmitButton;

      if (!sourceButton || !this.#stickySubmitButton) return;

      const isDisabled =
        sourceButton.hasAttribute('disabled') || sourceButton.getAttribute('aria-disabled') === 'true';

      this.#stickySubmitButton.toggleAttribute('disabled', isDisabled);
      this.#stickySubmitButton.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    }

    #setStickyLoading(isLoading) {
      if (!this.#stickySubmitButton) return;

      const spinner = this.#stickySubmitButton.querySelector('.loading__spinner');
      this.#stickySubmitButton.classList.toggle('loading', isLoading);
      this.#stickySubmitButton.toggleAttribute('aria-disabled', isLoading);

      if (spinner) {
        spinner.classList.toggle('hidden', !isLoading);
      }
    }
  }

  customElements.define('pbh-sticky-buy-bar', PbhStickyBuyBar);
})();
